/**
 * ATS Integration Manager Component
 * Admin interface for managing ATS integration settings, user import, and magic links
 * 
 * Refactored: Split into smaller sub-components for maintainability
 * - ATSSettingsTab: API key, SSO secret, configuration
 * - ATSMagicLinksTab: Generate magic login links
 * - ATSImportTab: Bulk import from Excel/CSV
 * - ATSUsersTab: User management table
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { Globe, Loader2 } from 'lucide-react';

// Import sub-components
import ATSSettingsTab from './ats/ATSSettingsTab';
import ATSMagicLinksTab from './ats/ATSMagicLinksTab';
import ATSImportTab from './ats/ATSImportTab';
import ATSUsersTab from './ats/ATSUsersTab';
import ATSDocsDialog from './ats/ATSDocsDialog';

const ATSIntegration = ({ api }) => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('settings');
    
    // Docs dialog state
    const [showDocs, setShowDocs] = useState(false);
    const [integrationDocs, setIntegrationDocs] = useState(null);

    const fetchSettings = useCallback(async () => {
        try {
            const response = await api.get('/ats/settings');
            setSettings(response.data);
        } catch (error) {
            toast.error('Failed to load ATS settings');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const fetchIntegrationDocs = async () => {
        try {
            const response = await api.get('/ats/docs/integration-guide');
            setIntegrationDocs(response.data);
            setShowDocs(true);
        } catch (error) {
            toast.error('Failed to load integration docs');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-48">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5 text-primary" />
                    ATS Integration
                </CardTitle>
                <CardDescription>
                    Integrate with your Applicant Tracking System for seamless user management
                </CardDescription>
            </CardHeader>
            <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid grid-cols-4 w-full mb-6">
                        <TabsTrigger value="settings" data-testid="ats-tab-settings">
                            Settings
                        </TabsTrigger>
                        <TabsTrigger value="magic-links" data-testid="ats-tab-magic-links">
                            Magic Links
                        </TabsTrigger>
                        <TabsTrigger value="import" data-testid="ats-tab-import">
                            Import Users
                        </TabsTrigger>
                        <TabsTrigger value="users" data-testid="ats-tab-users">
                            ATS Users
                        </TabsTrigger>
                    </TabsList>

                    {/* Settings Tab */}
                    <TabsContent value="settings">
                        <ATSSettingsTab 
                            api={api}
                            settings={settings}
                            setSettings={setSettings}
                            onFetchDocs={fetchIntegrationDocs}
                        />
                    </TabsContent>

                    {/* Magic Links Tab */}
                    <TabsContent value="magic-links">
                        <ATSMagicLinksTab 
                            api={api}
                            settings={settings}
                        />
                    </TabsContent>

                    {/* Import Tab */}
                    <TabsContent value="import">
                        <ATSImportTab api={api} />
                    </TabsContent>

                    {/* ATS Users Tab */}
                    <TabsContent value="users">
                        <ATSUsersTab api={api} />
                    </TabsContent>
                </Tabs>

                {/* Integration Docs Dialog */}
                <ATSDocsDialog 
                    open={showDocs}
                    onOpenChange={setShowDocs}
                    docs={integrationDocs}
                />
            </CardContent>
        </Card>
    );
};

export default ATSIntegration;
